/**
 * Owner scheduled-actions list for S08 (COP-074/COP-075/COP-077, HUB-024).
 *
 * Data fetch intentionally uses direct typed HubClient calls instead of the
 * Refine data provider: schedules and notifications are owner-scoped user
 * surfaces, not admin cross-owner resources. The Hub route remains the owner
 * isolation boundary; the SPA does not client-filter rows by owner.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  NotificationOwnerDto,
  ScheduleOwnerDto,
  ScheduleRunSummaryDto,
  TfTrackRow,
} from '@verybigthings/trackframe-shared/schemas';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { hub } from '@/lib/hub-client';
import { useUrlState } from '@/lib/url-state';
import {
  buildScheduleListQuery,
  mapScheduleApiError,
  type ScheduleEnabledFilter,
} from './schedule-ui-model';
import { ScheduleFormDialog } from './FormDialog';
import { RunHistory } from './RunHistory';
import { SchedulesListView, type BusyAction, type ScheduleListRow } from './list-view';
// Plan 02-06 (D-26 / D-28): "Created in chat" provenance badge for
// copilot-created rows. Re-exports moved out to a tiny self-contained
// module so unit tests can pin the DOM without booting the HubClient.
export {
  CreatedInChatBadge,
  CREATED_IN_CHAT_TOOLTIP,
} from './created-in-chat-badge';

const VERSION_CONFLICT_MESSAGE = 'This schedule changed since you opened it. Refresh and try again.';

// Module-level: these defaults are a dependency of the `useUrlState` setter,
// and only non-default values are written to the query string.
const FILTER_DEFAULTS = { enabled: 'all', trackId: '' };

function useSchedulesList() {
  // Not a refine table, so nothing syncs the toolbar to the location for us —
  // without this the filters reset on every reload.
  const [filters, setFilters] = useUrlState(FILTER_DEFAULTS);
  const enabledFilter = filters.enabled as ScheduleEnabledFilter;
  const trackIdFilter = filters.trackId;
  const setEnabledFilter = (value: ScheduleEnabledFilter) => setFilters({ enabled: value });
  const setTrackIdFilter = (value: string) => setFilters({ trackId: value });
  const [rows, setRows] = useState<ScheduleListRow[]>([]);
  const [tracks, setTracks] = useState<TfTrackRow[]>([]);
  const [notifications, setNotifications] = useState<NotificationOwnerDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listError, setListError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [tracksWarning, setTracksWarning] = useState<string | null>(null);
  const [notificationsWarning, setNotificationsWarning] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<BusyAction | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ScheduleListRow | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [formTarget, setFormTarget] = useState<ScheduleListRow | null>(null);
  const [historyTarget, setHistoryTarget] = useState<ScheduleListRow | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setListError(null);
    setActionError(null);

    const schedulesPromise = hub.schedulesList({
      query: buildScheduleListQuery({
        enabledFilter,
        trackId: trackIdFilter || undefined,
      }),
    });
    const tracksPromise = hub.tracksList();
    const notificationsPromise = hub.notificationsList({ query: { limit: 20 } });

    const [schedulesResult, tracksResult, notificationsResult] = await Promise.allSettled([
      schedulesPromise,
      tracksPromise,
      notificationsPromise,
    ]);

    if (schedulesResult.status === 'fulfilled') {
      setRows(schedulesResult.value.items.map(toScheduleListRow));
    } else {
      setListError(mapScheduleApiError(schedulesResult.reason));
    }

    if (tracksResult.status === 'fulfilled') {
      setTracks(tracksResult.value);
      setTracksWarning(null);
    } else {
      setTracksWarning('Track labels could not be loaded; showing track ids instead.');
    }

    if (notificationsResult.status === 'fulfilled') {
      setNotifications(notificationsResult.value.items);
      setNotificationsWarning(null);
    } else {
      setNotifications([]);
      setNotificationsWarning('Scheduler notifications could not be loaded.');
    }

    setIsLoading(false);
  }, [enabledFilter, trackIdFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const trackById = useMemo(() => new Map(tracks.map((track) => [track.id, track])), [tracks]);

  const toggleEnabled = async (row: ScheduleListRow) => {
    const nextEnabled = !row.schedule.enabled;
    setBusyAction({ id: row.schedule.id, action: nextEnabled ? 'resume' : 'pause' });
    setActionError(null);
    try {
      const response = await hub.schedulesUpdate({
        params: { id: row.schedule.id },
        body: {
          expectedUpdatedAt: row.schedule.updatedAt,
          enabled: nextEnabled,
        },
      });
      setRows((current) => current.map((item) => (
        item.schedule.id === row.schedule.id
          ? { schedule: response.schedule, latestRun: response.latestRun }
          : item
      )));
    } catch (error) {
      const mapped = mapScheduleApiError(error);
      setActionError(mapped);
      if (mapped === VERSION_CONFLICT_MESSAGE) await refresh();
    } finally {
      setBusyAction(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyAction({ id: deleteTarget.schedule.id, action: 'delete' });
    setActionError(null);
    try {
      await hub.schedulesDelete({ params: { id: deleteTarget.schedule.id } });
      setDeleteTarget(null);
      await refresh();
    } catch (error) {
      setActionError(mapScheduleApiError(error));
      await refresh();
    } finally {
      setBusyAction(null);
    }
  };

  const openCreateDialog = () => {
    setFormTarget(null);
    setFormMode('create');
  };

  const openEditDialog = (row: ScheduleListRow) => {
    setFormTarget(row);
    setFormMode('edit');
  };

  const closeFormDialog = () => {
    setFormMode(null);
    setFormTarget(null);
  };

  const upsertSavedSchedule = (response: { schedule: ScheduleOwnerDto; latestRun: ScheduleRunSummaryDto | null }) => {
    const savedRow = { schedule: response.schedule, latestRun: response.latestRun };
    setRows((current) => {
      const existingIndex = current.findIndex((item) => item.schedule.id === response.schedule.id);
      if (existingIndex === -1) return [savedRow, ...current];
      return current.map((item) => (item.schedule.id === response.schedule.id ? savedRow : item));
    });
    void refresh();
  };

  return {
    enabledFilter,
    setEnabledFilter,
    trackIdFilter,
    setTrackIdFilter,
    rows,
    tracks,
    trackById,
    notifications,
    isLoading,
    listError,
    actionError,
    tracksWarning,
    notificationsWarning,
    busyAction,
    deleteTarget,
    formMode,
    formTarget,
    historyTarget,
    refresh,
    toggleEnabled,
    confirmDelete,
    openCreateDialog,
    openEditDialog,
    closeFormDialog,
    upsertSavedSchedule,
    setHistoryTarget,
    setDeleteTarget,
  };
}

export function SchedulesList() {
  const state = useSchedulesList();
  return (
    <>
      <SchedulesListView {...state} />
      <ConfirmDialog
        open={!!state.deleteTarget}
        title="Delete scheduled action?"
        description={state.deleteTarget ? `The schedule ${state.deleteTarget.schedule.scheduleExpr} will be disabled and soft-deleted.` : ''}
        confirmLabel="Delete"
        destructive
        busy={state.busyAction?.action === 'delete'}
        onCancel={() => state.setDeleteTarget(null)}
        onConfirm={() => void state.confirmDelete()}
      />
      <ScheduleFormDialog
        open={state.formMode !== null}
        mode={state.formMode ?? 'create'}
        schedule={state.formTarget?.schedule ?? null}
        initialTracks={state.tracks}
        onClose={state.closeFormDialog}
        onSaved={state.upsertSavedSchedule}
        onVersionConflict={() => state.refresh()}
      />
      <RunHistory
        open={state.historyTarget !== null}
        schedule={state.historyTarget?.schedule ?? null}
        onClose={() => state.setHistoryTarget(null)}
      />
    </>
  );
}

function toScheduleListRow(schedule: ScheduleOwnerDto): ScheduleListRow {
  const maybeLatestRun = (schedule as ScheduleOwnerDto & { latestRun?: ScheduleRunSummaryDto | null }).latestRun ?? null;
  return { schedule, latestRun: maybeLatestRun };
}
